import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { debounceTime, firstValueFrom, Subject, Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { FilterService } from '../../services/filter/filter.service';
import { LoaderService } from '../../services/loader.service';
import { PaginationService } from '../../services/pagination/paginationcard/pagination.service';
import { ModalResidenceCertificateComponent } from '../modal-residence-certificate/modal-residence-certificate.component';

import { TwoFactorAuthService } from '../../services/two-factor/two-factor-auth.service';
import Swal from 'sweetalert2';
import { ProjectsDataService } from '../../services/projects-data.service';

@Component({
  selector: 'app-cards-real-estate',
  templateUrl: './cards-real-estate.component.html',
  styleUrls: ['./cards-real-estate.component.css'],
})
export class CardsRealEstateComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  cards: any[] = []; // Todas las propiedades cargadas
  filteredCards: any[] = []; // Propiedades filtradas
  paginatedCards: any[] = []; // Propiedades visibles en la página actual
  isInversor: boolean = false; // Indica si el usuario es inversor
  userWallets: string[] | string = ''; // Wallets del usuario
  loadingButtons: boolean = false; // Indicador para botones de carga
  isLoading: boolean = false; // Indicador de carga
  showPaginationControls: boolean = false; // Controla si mostrar los botones de paginación
  showFilterBar: boolean = false;
  isMobile: boolean = false;
  isTablet: boolean = false;
  currentPage: number = 0;
  totalPages: number = 0;
  itemCount: number = 0;
  name: string = '';
  status: string = '';
  priceRange: [number, number | string] | null = null;
  activationDate: Date | null = null;
  orderBy: string = '';
  startDate: Date | null = null;
  endDate: Date | null = null;

  private readonly subscriptions: Subscription = new Subscription(); // Suscripciones para limpiar después
  private resizeHandler: (() => void) | null = null; // Handler para el resize event

  // Propiedades para controlar el estado de carga
  private dataLoaded = false;
  private viewRendered = false;
  private modalChecked = false; // Flag para evitar verificar el modal múltiples veces
  private readonly MODAL_SHOWN_KEY = 'residenceCertificateModalShown'; // Key para sessionStorage

  /** DEBUG: true = forzar modal visible. Poner a false antes de commit. */
  private readonly FORCE_RESIDENCE_MODAL = false;

  constructor(
    private readonly afs: AngularFirestore,
    private readonly authSrv: AuthService,
    private readonly crf: ChangeDetectorRef,
    private readonly paginationService: PaginationService,
    private readonly loaderService: LoaderService,
    private readonly filterService: FilterService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly projectsDataService: ProjectsDataService,
    private readonly twoFactorService: TwoFactorAuthService
  ) {}

  private checkScreenSize() {
    const width = window.innerWidth;

    // Definir rangos de dispositivos específicos
    this.isMobile = width <= 576; // Solo móviles pequeños
    this.isTablet = width > 576 && width <= 1366; // Tablets e iPads (ampliado hasta 1366px)

    // Por defecto, ocultar la barra de filtros en móvil y tablet
    if (this.isMobile || this.isTablet) {
      this.showFilterBar = false;
    } else {
      // Mostrar la barra de filtros desktop en pantallas grandes
      this.showFilterBar = true;
    }

    this.crf.detectChanges();
  }

  toggleFilterBar() {
    if (this.isMobile || this.isTablet) {
      this.showFilterBar = !this.showFilterBar;
      this.crf.detectChanges();
    }
  }

  ngAfterViewChecked() {
    if (this.dataLoaded && !this.viewRendered && this.cards.length > 0) {
      if (this.paginatedCards.length > 0) {
        this.viewRendered = true;
        this.checkIfComplete();
      }
    }
  }

  private checkIfComplete() {
    if (!this.dataLoaded) return;

    const hasCards = this.cards.length > 0;
    const hasPaginated = this.paginatedCards.length > 0;

    if (hasCards && hasPaginated) {
      // Dar tiempo extra para que Angular termine de renderizar
      setTimeout(() => {
        this.loaderService.hideSpinner();
        this.isLoading = false;
      }, 500);
    }
  }

  filters$ = new Subject<any>();

  // Método para manejar navegación de vuelta
  onActivate() {
    if (this.cards.length === 0) {
      // Si no hay datos, cargar desde cero
      this.getCards();
    } else {
      // Ya hay datos: asegurarnos de que paginación y spinner se sincronicen
      this.dataLoaded = true;
      this.updatePaginatedCards();

      // Si ya hay tarjetas visibles, consideramos la vista lista
      if (this.paginatedCards.length > 0) {
        this.viewRendered = true;
        this.loaderService.hideSpinner();
        this.isLoading = false;
      } else {
        // Fallback: seguir usando la lógica de checkIfComplete
        this.checkIfComplete();
      }
    }
  }

  ngOnInit(): void {
    // Resetear estados de carga al inicializar
    this.dataLoaded = false;
    this.viewRendered = false;
    this.isLoading = true;

    // ----- Verificación 2FA de entrada -----
    const pendingAuthData = sessionStorage.getItem('pending2FAAuth');
    if (pendingAuthData) {
      setTimeout(() => this.promptForEntryMfa(), 100);
    }
    // ---------------------------------------

    // Configurar el listener de resize
    const resizeHandler = () => this.checkScreenSize();
    window.addEventListener('resize', resizeHandler);

    // Guardar referencia para limpiar después
    this.resizeHandler = resizeHandler;

    this.checkScreenSize();

    // Debug log for initialization
    // console.log('CardsRealEstateComponent initialized');

    // Mostrar spinner de carga inicial inmediatamente
    this.loaderService.showSpinner('Cargando propiedades');

    // Cargar propiedades desde Firebase
    this.getCards();

    // Configurar la paginación para mostrar 9 propiedades por página
    this.paginationService.setPageSize(9);
    // console.log('Page size set to:', this.paginationService.getPageSize());

    // Escuchar cambios en la página actual
    this.subscriptions.add(
      this.paginationService.currentPage$.subscribe(() => {
        // console.log('Current page changed');
        this.updatePaginatedCards();
      }),
    );

    // Escuchar cambios en el estado del loader
    this.subscriptions.add(
      this.loaderService.dataIsLoading.subscribe((loading) => {
        // Solo actualizar si no estamos en proceso de carga inicial
        if (!this.dataLoaded) {
          this.isLoading = loading?.isLoading ?? false;
        }
        // console.log('Loading state:', this.isLoading);
        this.crf.detectChanges();
      }),
    );

    // Escuchar cambios en los datos filtrados desde el servicio de filtros
    this.subscriptions.add(
      this.filterService.filteredData$.subscribe((filteredData) => {
        // console.log('Filtered data received:', filteredData.length);
        this.filteredCards = filteredData;
        this.updatePaginatedCards(); // Actualizar la paginación con los nuevos datos
      }),
    );
    this.filters$.pipe(debounceTime(300)).subscribe((filters) => {
      this.applyFilters(filters);
    });

    // Verificar si debe mostrarse el modal de certificado de residencia fiscal
    // Solo mostrar una vez por sesión después del login/registro
    const delay = this.FORCE_RESIDENCE_MODAL ? 500 : 2000;
    const shouldCheckModal = () =>
      this.FORCE_RESIDENCE_MODAL ||
      !sessionStorage.getItem(this.MODAL_SHOWN_KEY);

    const currentUser = this.authSrv.userAuth.getValue();
    if (currentUser?.uid && !this.modalChecked) {
      this.modalChecked = true;
      if (shouldCheckModal()) {
        setTimeout(() => this.checkAndShowResidenceCertificateModal(), delay);
      }
    } else {
      this.subscriptions.add(
        this.authSrv.userAuth.subscribe((user: { uid?: string } | null) => {
          if (user && user.uid && !this.modalChecked) {
            this.modalChecked = true;
            if (shouldCheckModal()) {
              setTimeout(() => this.checkAndShowResidenceCertificateModal(), delay);
            }
          }
        }),
      );
    }
  }

  onFilterChange() {
    this.filters$.next({
      name: this.name,
      status: this.status,
      startDate: this.startDate,
      endDate: this.endDate,
      orderBy: this.orderBy,
    });
  }
  /**
   * Obtiene las propiedades desde Firebase y las envía al servicio de filtros.
   */
  /**
   * Obtiene las propiedades desde ProjectsDataService (API/Firestore) y las envía al servicio de filtros.
   */
  getCards() {
    // Resetear estados de carga
    this.dataLoaded = false;
    this.viewRendered = false;

    // Mostrar spinner inmediatamente
    this.loaderService.showSpinner('Cargando propiedades');
    this.isLoading = true;

    // Usar el servicio unificado (API co fallback a Firestore)
    this.subscriptions.add(
      this.projectsDataService.getProjects().subscribe({
        next: async (res: any) => {
          if (res !== undefined && res !== null) {
            this.isInversor = false;
            const user = await this.authSrv.userAuth.getValue();
            if (user?.uid) {
              const result = await this.afs
                .collection('users')
                .doc(user.uid)
                .get();
              const parseResult: any = await result.toPromise();
              this.isInversor = parseResult?.data()?.isInversorPro ?? false;
            }

            // Clasificar y ordenar propiedades
            // Nota: Aunque la API ya devuelve ordenado, mantenemos esto por seguridad
            // si el fallback a Firestore se activa o el orden cambia.
            this.cards = res.sort(
              (a: any, b: any) => Number(b.id) - Number(a.id),
            );

            this.filterService.loadData(this.cards); // Pasar propiedades al servicio de filtros
            this.updatePaginatedCards(); // Actualizar la paginación inicial

            // Marcar que los datos están cargados
            this.dataLoaded = true;

            // Ocultar spinner inmediatamente después de cargar los datos
            // Usar setTimeout para asegurar que Angular detecte los cambios
            setTimeout(() => {
              this.loaderService.hideSpinner();
              this.isLoading = false;
              this.crf.detectChanges();
              this.checkIfComplete();
            }, 100);
          } else {
            // Si no hay datos, ocultar spinner de todas formas
            this.dataLoaded = true;
            setTimeout(() => {
              this.loaderService.hideSpinner();
              this.isLoading = false;
              this.crf.detectChanges();
            }, 100);
          }
        },
        error: (error) => {
          console.error('❌ Error loading cards:', error);
          this.isLoading = false;
          this.loaderService.hideSpinner();
          this.crf.detectChanges();
        },
      }),
    );
  }
  appyingFilters = false;

  resetFilters() {
    // Mostrar spinner al resetear filtros
    this.loaderService.showSpinner('Reseteando filtros...');
    this.isLoading = true;

    this.name = '';
    this.status = '';
    this.priceRange = null;
    this.activationDate = null;
    this.startDate = null;
    this.endDate = null;
    this.orderBy = '';
    this.applyFilters({ dateRange: null, startDate: null, endDate: null });
    this.appyingFilters = false;
    this.toggleFilterBar();

    // Ocultar spinner después de un breve delay
    setTimeout(() => {
      this.loaderService.hideSpinner();
      this.isLoading = false;
    }, 300);
  }
  /**
   * Aplica los filtros y actualiza la paginación.
   */
  applyFilters(filters: {
    name?: string;
    status?: string;
    // priceRange?: [number, number | string];
    dateRange: Date | null;
    startDate: Date | null;
    endDate: Date | null;
    orderBy?: string;
  }): void {
    // Mostrar spinner cuando se aplican filtros
    this.loaderService.showSpinner('Aplicando filtros...');
    this.isLoading = true;

    this.appyingFilters = true;
    this.filterService.updateFilters(filters); // Actualizamos los filtros
    this.paginationService.setPage(0); // Reseteamos la página a la primera
    this.updatePaginatedCards(); // Actualizamos las propiedades visibles

    // Ocultar spinner después de un pequeño delay
    setTimeout(() => {
      this.loaderService.hideSpinner();
      this.isLoading = false;
    }, 300);
    // this.toggleFilterBar();
  }

  /**
   * Actualiza las propiedades visibles según la paginación y los filtros aplicados.
   */
  private updatePaginatedCards() {
    const startIndex =
      this.paginationService.getCurrentPage() *
      this.paginationService.getPageSize();
    const endIndex = startIndex + this.paginationService.getPageSize();

    // Usar filteredCards si hay datos filtrados; si no, usar cards
    const sourceData = this.appyingFilters ? this.filteredCards : this.cards;
    // Debug logs for pagination details
    // console.log('Source data total:', sourceData.length);
    // console.log('Page size:', this.paginationService.getPageSize());
    // console.log('Current page:', this.paginationService.getCurrentPage());
    // console.log('Start index:', startIndex);
    // console.log('End index:', endIndex);

    this.paginatedCards = sourceData.slice(startIndex, endIndex); // Propiedades en la página actual

    // console.log('Paginated cards count:', this.paginatedCards.length);

    // Condition to show pagination controls
    this.showPaginationControls =
      sourceData.length > this.paginationService.getPageSize();

    this.itemCount = sourceData.length;
    this.currentPage = this.paginationService.getCurrentPage() + 1;
    this.totalPages = this.getTotalPages();

    console.log('Updating paginated cards - Current page:', this.currentPage);
    console.log('Updating paginated cards - Total pages:', this.totalPages);
    console.log(
      'Updating paginated cards - Show pagination:',
      this.showPaginationControls,
    );

    // console.log('Show pagination controls:', this.showPaginationControls);
    // console.log(
    //   'Pagination condition:',
    //   sourceData.length,
    //   '>',
    //   this.paginationService.getPageSize()
    // );

    this.crf.detectChanges();
  }

  /**
   * Cambia a la página siguiente.
   */
  goToNextPage() {
    // Mostrar spinner brevemente al cambiar de página
    this.loaderService.showSpinner('Cargando página...');
    this.isLoading = true;

    const sourceData =
      this.filteredCards.length > 0 ? this.filteredCards : this.cards;
    this.paginationService.nextPage(sourceData.length);
    // console.log('Next page clicked');
    this.updatePaginatedCards();

    // Ocultar spinner después de un breve delay
    setTimeout(() => {
      this.loaderService.hideSpinner();
      this.isLoading = false;
    }, 200);
  }

  /**
   * Cambia a la página anterior.
   */
  goToPreviousPage() {
    // Mostrar spinner brevemente al cambiar de página
    this.loaderService.showSpinner('Cargando página...');
    this.isLoading = true;

    this.paginationService.previousPage();
    // console.log('Previous page clicked');
    this.updatePaginatedCards();

    // Ocultar spinner después de un breve delay
    setTimeout(() => {
      this.loaderService.hideSpinner();
      this.isLoading = false;
    }, 200);
  }

  /**
   * Calculates the total number of pages based on the data length and page size.
   */
  getTotalPages(): number {
    const sourceData =
      this.filteredCards.length > 0 ? this.filteredCards : this.cards;
    return Math.ceil(sourceData.length / this.paginationService.getPageSize());
  }

  /**
   * Generates an array representing the page numbers.
   */
  getPages(): number[] {
    const totalPages = this.getTotalPages();
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  /**
   * Checks if a given page index is the current page.
   */
  isCurrentPage(pageIndex: number): boolean {
    return this.paginationService.getCurrentPage() === pageIndex;
  }

  /**
   * Navigates to a specific page by index.
   */
  goToPage(pageIndex: number): void {
    // Mostrar spinner brevemente al cambiar de página
    this.loaderService.showSpinner('Cargando página...');
    this.isLoading = true;

    this.paginationService.setPage(pageIndex);
    this.updatePaginatedCards();

    // Ocultar spinner después de un breve delay
    setTimeout(() => {
      this.loaderService.hideSpinner();
      this.isLoading = false;
    }, 200);
  }

  ngOnDestroy() {
    // Asegurar que el spinner se oculte al destruir el componente
    this.loaderService.hideSpinner();
    this.isLoading = false;

    // Resetear estados de carga
    this.dataLoaded = false;
    this.viewRendered = false;

    // Limpiar todas las suscripciones
    this.subscriptions.unsubscribe();

    // Limpiar el event listener de resize
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    // console.log('Component destroyed, subscriptions unsubscribed');
  }

  public isFirstPage(): boolean {
    const isFirst = this.paginationService.getCurrentPage() === 0;
    // console.log('Is first page:', isFirst);
    return isFirst;
  }

  public isLastPage(): boolean {
    const sourceData =
      this.filteredCards.length > 0 ? this.filteredCards : this.cards;
    const isLast =
      (this.paginationService.getCurrentPage() + 1) *
        this.paginationService.getPageSize() >=
      sourceData.length;
    // console.log('Is last page:', isLast);
    return isLast;
  }

  public getCurrentPage(): number {
    const currentPage = this.paginationService.getCurrentPage();
    // console.log('Current page number:', currentPage);
    return currentPage;
  }

  /**
   * Verifica si debe mostrarse el modal de certificado de residencia fiscal
   */
  private async checkAndShowResidenceCertificateModal(): Promise<void> {
    try {
      const user = this.authSrv.userAuth.getValue();
      if (!user || !user.uid) {
        console.log('[Modal Residencia] No hay usuario autenticado');
        return;
      }

      // DEBUG: forzar modal siempre visible para depuración
      if (this.FORCE_RESIDENCE_MODAL) {
        console.log('[Modal Residencia] FORCE_RESIDENCE_MODAL=true - forzando apertura');
        setTimeout(() => this.openResidenceCertificateModal(user.uid), 500);
        return;
      }

      console.log('[Modal Residencia] Verificando para usuario:', user.uid);

      // Obtener datos del usuario
      const userDoc = await firstValueFrom(
        this.afs.collection('users').doc(user.uid).get(),
      );

      if (!userDoc.exists) {
        console.log('[Modal Residencia] No existe documento de usuario');
        return;
      }

      const userData = userDoc.data() as any;

      // Solo mostrar el modal cuando el KYC esté completo (aprobado). No mostrar en mitad del proceso
      // si el usuario se desloguea y vuelve a entrar.
      const kycCompleted = userData?.kyc?.result === 1;
      if (!kycCompleted) {
        console.log('[Modal Residencia] KYC no completado (kyc.result !== 1), no se muestra el modal');
        return;
      }

      const dontShowValue = userData?.dontShowResidenceCertificateModal;

      console.log('[Modal Residencia] Datos del usuario:', {
        dontShowResidenceCertificateModal: dontShowValue,
        tipo: typeof dontShowValue,
        esTrue: dontShowValue === true,
        esTruthy: !!dontShowValue,
        valorCompleto: userData,
      });

      // Verificar si el usuario ha marcado "no volver a mostrar"
      // Solo considerar true si es explícitamente true (boolean), no otros valores truthy
      // También verificar si es string "true" (por si está guardado como string)
      // Si el valor es string "true", lo corregimos guardándolo como boolean false para resetearlo
      const shouldNotShow = dontShowValue === true || dontShowValue === 'true';

      if (shouldNotShow) {
        console.log(
          '[Modal Residencia] Usuario marcó "no volver a mostrar" - Valor:',
          dontShowValue,
          'Tipo:',
          typeof dontShowValue,
        );

        // Si el valor es string "true" en lugar de boolean, es un error de datos
        // Lo corregimos automáticamente estableciéndolo como false
        if (dontShowValue === 'true' && typeof dontShowValue === 'string') {
          console.log(
            '[Modal Residencia] CORRECCIÓN: Valor está como string "true", corrigiendo a boolean false',
          );
          try {
            await this.afs.collection('users').doc(user.uid).update({
              dontShowResidenceCertificateModal: false,
            });
            console.log('[Modal Residencia] Valor corregido exitosamente');
            // Continuar para mostrar el modal ya que lo corregimos
          } catch (error) {
            console.error(
              '[Modal Residencia] Error al corregir el valor:',
              error,
            );
            return; // Si no se puede corregir, no mostrar el modal
          }
        } else {
          // Si es boolean true, respetar la preferencia del usuario
          return;
        }
      }

      // Si el valor es undefined, null, false o no existe, continuar comprobaciones
      console.log(
        '[Modal Residencia] dontShowResidenceCertificateModal no impide mostrar:',
        dontShowValue,
      );

      // Solo mostrar si el país de residencia es diferente a España
      const userCountry =
        userData?.Empresa?.Datos_Empresa?.Pais_Fiscal ||
        userData?.Empresa?.Datos_Empresa?.pais_de_residencia ||
        userData?.particular?.pais_de_residencia ||
        userData?.Empresa?.Representante?.pais_de_residencia ||
        'España';

      if (userCountry === 'España') {
        console.log('[Modal Residencia] País de residencia es España, no se muestra el modal');
        return;
      }

      // No mostrar si el usuario ya tiene certificados de residencia fiscal
      const fiscalDocsSnapshot = await firstValueFrom(
        this.afs
          .collection('fiscal-documents', (ref) =>
            ref.where('userId', '==', user.uid),
          )
          .get(),
      );
      const hasCertificates =
        !fiscalDocsSnapshot.empty && fiscalDocsSnapshot.docs.length > 0;
      if (hasCertificates) {
        console.log(
          '[Modal Residencia] Usuario ya tiene certificados fiscales, no se muestra el modal',
        );
        return;
      }

      // Verificar si el modal ya se mostró en esta sesión
      const modalShown = sessionStorage.getItem(this.MODAL_SHOWN_KEY);
      if (modalShown) {
        console.log('[Modal Residencia] Modal ya se mostró en esta sesión, no se vuelve a mostrar');
        return;
      }

      setTimeout(() => {
        this.openResidenceCertificateModal(user.uid);
      }, 500);
    } catch (error) {
      console.error(
        '[Modal Residencia] Error al verificar modal de certificado de residencia:',
        error,
      );
    }
  }

  /**
   * Abre el modal de certificado de residencia fiscal (con "No volver a mostrar" y dontShowResidenceCertificateModal)
   */
  private openResidenceCertificateModal(userId: string): void {
    console.log('[Modal Residencia] Abriendo modal para usuario:', userId);

    try {
      const dialogRef = this.dialog.open(ModalResidenceCertificateComponent, {
        width: '480px',
        maxWidth: '90vw',
        disableClose: false,
        autoFocus: true,
        panelClass: 'modal-residence-certificate',
        data: {
          onDontShowAgain: async (dontShow: boolean) => {
            console.log('[Modal Residencia] Guardando preferencia:', dontShow);
            try {
              await this.afs.collection('users').doc(userId).update({
                dontShowResidenceCertificateModal: dontShow,
              });
              console.log(
                '[Modal Residencia] Preferencia guardada exitosamente',
              );
            } catch (error) {
              console.error(
                '[Modal Residencia] Error al guardar preferencia del modal:',
                error,
              );
              // Intentar con set y merge si el campo no existe
              try {
                await this.afs.collection('users').doc(userId).set(
                  {
                    dontShowResidenceCertificateModal: dontShow,
                  },
                  { merge: true },
                );
                console.log(
                  '[Modal Residencia] Preferencia guardada con merge',
                );
              } catch (mergeError) {
                console.error(
                  '[Modal Residencia] Error al guardar la preferencia del modal (merge):',
                  mergeError,
                );
              }
            }
          },
        },
      });

      console.log('[Modal Residencia] Modal abierto, esperando cierre...');

      // Marcar que el modal se mostró en esta sesión
      sessionStorage.setItem(this.MODAL_SHOWN_KEY, 'true');

      dialogRef.afterClosed().subscribe((result) => {
        console.log('[Modal Residencia] Modal cerrado con resultado:', result);
        if (result && result.dontShowAgain && result.action === 'cancel') {
          // Si canceló y marcó "no volver a mostrar", ya se guardó en onDontShowAgain
          // No necesitamos hacer nada más
        }
      });
    } catch (error) {
      console.error('[Modal Residencia] Error al abrir el modal:', error);
    }
  }

  private async promptForEntryMfa(): Promise<void> {
    const result = await Swal.fire({
      title: 'Validación de Seguridad',
      text: 'Tu sesión segura requiere validación. Ingresa tu código TOTP (Google Authenticator) para acceder a la plataforma:',
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off',
        pattern: '[0-9]*',
        inputmode: 'numeric',
        maxlength: '6'
      },
      showCancelButton: true,
      confirmButtonText: 'Verificar y Continuar',
      cancelButtonText: 'Cancelar transacción',
      confirmButtonColor: '#0093A2',
      showLoaderOnConfirm: true,
      backdrop: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      preConfirm: async (code) => {
        if (!code || code.length < 6) {
          Swal.showValidationMessage('Ingresa los 6 dígitos completos');
          return false;
        }
        try {
          const isValid = await this.twoFactorService.verifyTokenAPI(code);
          if (!isValid) {
            throw new Error('Código incorrecto');
          }
          // Refrescar el token local para obtener nuevos claims y persistir la sesion validada
          try {
             const currentUser = await this.authSrv.userAuth.getValue();
             if (currentUser) await currentUser.getIdToken(true);
             else await this.authSrv.getJWTToken();
          } catch (e) {
             await this.authSrv.getJWTToken();
          }
          return true;
        } catch (err) {
          Swal.showValidationMessage('Código incorrecto o expirado');
          return false;
        }
      }
    });

    if (result.isConfirmed && result.value === true) {
      // Verificación exitosa, limpiar estado temporal
      sessionStorage.removeItem('pending2FAAuth');
      
      const pendingAuthDataStr = sessionStorage.getItem('pending2FAAuth');
      // Aseguramos de completar logicamente el login en authservice si hiciera falta
      // this.authSrv.completeLoginAfter2FA(...); 
      // Dado que authSrv está listo, solo quitamos la barrera y refrescamos los datos if needed.
    } else {
      // Si cancela, lo sacamos de la sesión completamente
      sessionStorage.removeItem('pending2FAAuth');
      this.authSrv.logout();
      this.router.navigateByUrl('/login');
    }
  }
}

